import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { TableCell } from '@tiptap/extension-table';
import { PluginKey } from '@tiptap/pm/state';
import { Plugin } from '@tiptap/pm/state';
import { TableMap } from '@tiptap/pm/tables';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { CustomTableCell } from './cell-component';

export const CustomTableCellExtension = TableCell.extend<{
  readonly: boolean;
  isInsertOnlyMode: boolean;
}>({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.style.width || el.getAttribute('width'),
        renderHTML: (attrs) => {
          return attrs.width ? { style: `width: ${attrs.width};` } : {};
        },
      },
      dataType: {
        default: TableDataType.TEXT,
        parseHTML: (element) =>
          element.getAttribute('data-type') || TableDataType.TEXT,
        renderHTML: (attrs) => {
          return {
            'data-type': attrs.dataType, // always write current
          };
        },
      },
      readonly: {
        default: this.options.readonly,
        parseHTML: () => this.options.readonly,
        renderHTML: () => ({ 'data-read-only': this.options.readonly }),
      },

      isInsertOnlyMode: {
        default: this.options.isInsertOnlyMode,
        parseHTML: (element) =>
          this.options.isInsertOnlyMode
            ? true
            : element.getAttribute('data-insert-only') === 'true',
        renderHTML: (attrs) => {
          return attrs.isInsertOnlyMode ? { 'data-insert-only': 'true' } : {};
        },
      },
      ghost: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-ghost') === 'true',
        renderHTML: (attrs) => (attrs.ghost ? { 'data-ghost': 'true' } : {}),
      },
      choices: {
        default: [],
        parseHTML: (element) => {
          const raw = element.getAttribute('data-choices');

          if (!raw) {
            return [];
          }
          try {
            return JSON.parse(raw);
          } catch {
            return [];
          }
        },
        renderHTML: (attrs) =>
          attrs.choices && attrs.choices.length > 0
            ? { 'data-choices': JSON.stringify(attrs.choices) }
            : {},
      },
      title: {
        default: '',
      },
      nodeAttrs: {
        default: {},
        parseHTML: (element) => {
          const dataId = element.getAttribute('data-id');

          return dataId ? { 'data-id': dataId } : {};
        },
        renderHTML: (attrs) => {
          if (attrs.nodeAttrs && attrs.nodeAttrs['data-id']) {
            return {
              'data-id': attrs.nodeAttrs['data-id'],
            };
          }

          return {};
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(CustomTableCell, {
      className: 'tiptap-table-cell',
      as: 'td',
    });
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('syncCellDataTypeWithHeader'),
        appendTransaction: (transactions, oldState, newState) => {
          let tr = newState.tr;
          let modified = false;

          // Also process all existing table cells on any document change
          newState.doc.descendants((node, pos) => {
            // Skip cells that have nodeAttrs['data-id'] (they belong to MULTI_QUESTION sections)
            const hasDataId = node.attrs.nodeAttrs?.['data-id'];

            if (
              node.type.name === 'tableCell' &&
              node.attrs.ghost === false &&
              !hasDataId
            ) {
              const $pos = newState.doc.resolve(pos);
              let tableNode: any = null;
              let tablePos = 0;
              let isInSectionWithOwnSync = false;

              // Walk up the tree to find the table node AND check for section parent
              for (let depth = $pos.depth; depth >= 0; depth--) {
                const parentNode = $pos.node(depth);

                if (parentNode.type.name === 'table') {
                  tableNode = parentNode;
                  tablePos = $pos.start(depth);
                }

                // Skip cells inside MULTI_QUESTION or QUESTION_ANSWER_ITEM; they are synced by syncMultiQuestionTableCells
                if (parentNode.type.name === 'RL_CUSTOM_SECTION') {
                  const sectionType = parentNode.attrs.sectionType;

                  if (
                    sectionType === 'MULTI_QUESTION' ||
                    sectionType === 'QUESTION_ANSWER_ITEM'
                  ) {
                    isInSectionWithOwnSync = true;
                  }
                }
              }

              if (isInSectionWithOwnSync) {
                return;
              }

              if (tableNode) {
                const map = TableMap.get(tableNode);
                const cellPosInTable = pos - (tablePos + 1);
                let rect: any = null;

                try {
                  rect = map.findCell(cellPosInTable);
                } catch {
                  // Use alternative approach if findCell fails
                  for (let i = 0; i < map.map.length; i++) {
                    const cellStart = map.map[i];
                    const cellEnd =
                      i + 1 < map.map.length
                        ? map.map[i + 1]
                        : tableNode.content.size;

                    if (
                      cellPosInTable >= cellStart &&
                      cellPosInTable < cellEnd
                    ) {
                      const row = Math.floor(i / map.width);
                      const col = i % map.width;

                      rect = {
                        top: row,
                        left: col,
                        bottom: row + 1,
                        right: col + 1,
                      };
                      break;
                    }
                  }
                }

                if (rect) {
                  const headerCellPos =
                    tablePos + map.positionAt(0, rect.left + 1, tableNode);
                  const headerCellNode = newState.doc.nodeAt(headerCellPos);

                  if (headerCellNode) {
                    // If header has config, use its dataType
                    if (headerCellNode.attrs?.config?.dataType) {
                      if (
                        node.attrs.dataType !==
                        headerCellNode.attrs.config.dataType
                      ) {
                        tr.setNodeMarkup(pos, undefined, {
                          ...node.attrs,
                          dataType: headerCellNode.attrs.config.dataType,
                          choices: headerCellNode.attrs.config.choices,
                          title: headerCellNode.attrs.config.name,
                        });
                        modified = true;
                      } else if (
                        node.attrs.title !== headerCellNode.attrs.config.name
                      ) {
                        tr.setNodeMarkup(pos, undefined, {
                          ...node.attrs,
                          title: headerCellNode.attrs.config.name,
                        });
                        modified = true;
                      }
                    } else {
                      // If header is ghost (no config), default to TEXT
                      if (node.attrs.dataType !== TableDataType.TEXT) {
                        tr.setNodeMarkup(pos, undefined, {
                          ...node.attrs,
                          dataType: TableDataType.TEXT,
                        });
                        modified = true;
                      }
                    }
                  }
                }
              }
            }
          });

          return modified ? tr : null;
        },
        props: {
          handleTextInput(view, from, to, text) {
            const $pos = view.state.selection.$from;
            const cell = $pos.node(-1); // get enclosing cell node

            if (cell?.attrs?.dataType === TableDataType.NUMBER) {
              if (!/^[0-9]*$/.test(text)) {
                return true;
              }
            }

            return false;
          },
          handlePaste(view, event, slice) {
            const $pos = view.state.selection.$from;
            const cell = $pos.node(-1);

            if (cell?.attrs?.dataType === TableDataType.TEXT) {
              const pastedText = slice.content.textBetween(
                0,
                slice.content.size,
                '',
              );

              if (!/^[0-9]*$/.test(pastedText)) {
                return true;
              }
            }

            return false;
          },
        },
      }),
      // Plugin to sync table cells with MULTI_QUESTION section config
      (() => {
        let lastUpdateTime = 0;
        const DEBOUNCE_MS = 10;

        return new Plugin({
          key: new PluginKey('syncMultiQuestionTableCells'),
          appendTransaction: (transactions, oldState, newState) => {
            // Skip if this transaction came from this plugin (prevent infinite loops)
            if (
              transactions.some((tr) =>
                tr.getMeta('syncMultiQuestionTableCells'),
              )
            ) {
              return null;
            }

            // Throttle updates to prevent overwhelming the editor
            const now = Date.now();

            if (now - lastUpdateTime < DEBOUNCE_MS) {
              return null;
            }

            // Check if any MULTI_QUESTION or QUESTION_ANSWER_ITEM sections exist
            let hasSectionsToSync = false;

            newState.doc.descendants((node) => {
              if (node.type.name === 'RL_CUSTOM_SECTION' && node.attrs.config) {
                const sectionType = node.attrs.sectionType;

                if (
                  sectionType === 'MULTI_QUESTION' ||
                  sectionType === 'QUESTION_ANSWER_ITEM'
                ) {
                  hasSectionsToSync = true;

                  return false; // Stop searching
                }
              }

              return true;
            });

            // If no sections found, nothing to do
            if (!hasSectionsToSync) {
              return null;
            }

            // Collect cells that need updating
            const cellsToUpdate: Array<{
              pos: number;
              attrs: any;
            }> = [];

            // Process all table cells
            newState.doc.descendants((node, pos) => {
              if (node.type.name === 'tableCell' && !node.attrs.ghost) {
                const dataId = node.attrs.nodeAttrs?.['data-id'];
                const $pos = newState.doc.resolve(pos);

                // Find parent section (MULTI_QUESTION or QUESTION_ANSWER_ITEM) and table
                let customSectionNode: any = null;
                let tableNode: any = null;
                let tablePos = 0;

                for (let depth = $pos.depth; depth >= 0; depth--) {
                  const parentNode = $pos.node(depth);

                  if (parentNode.type.name === 'table' && !tableNode) {
                    tableNode = parentNode;
                    tablePos = $pos.start(depth);
                  }

                  if (parentNode.type.name === 'RL_CUSTOM_SECTION') {
                    const sectionType = parentNode.attrs.sectionType;

                    if (
                      sectionType === 'MULTI_QUESTION' ||
                      sectionType === 'QUESTION_ANSWER_ITEM'
                    ) {
                      customSectionNode = parentNode;
                      break;
                    }
                  }
                }

                if (!customSectionNode || !customSectionNode.attrs.config) {
                  return;
                }

                const sectionType = customSectionNode.attrs.sectionType;
                const config = customSectionNode.attrs.config;

                // Check cell position (second column = answer column)
                if (!tableNode) {
                  return;
                }

                let rect: { top: number; left: number } | null = null;

                try {
                  const map = TableMap.get(tableNode);
                  const cellPosInTable = pos - tablePos;

                  rect = map.findCell(cellPosInTable);
                } catch {
                  return;
                }

                if (rect.left !== 1) {
                  return;
                }

                // QUESTION_ANSWER_ITEM: single question per section, answer cell is second row (row 1), second column
                if (sectionType === 'QUESTION_ANSWER_ITEM') {
                  if (rect.top !== 1) {
                    return;
                  }

                  const dataType = config.dataType ?? TableDataType.TEXT;
                  const title = config.question ?? '';
                  const baseChoices = config.choices ?? [];

                  const isChoiceType =
                    dataType === TableDataType.SingleSelect ||
                    dataType === TableDataType.MultiSelect;

                  const choices = isChoiceType
                    ? (() => {
                        const cellText = node.textContent ?? '';
                        const cellValues = cellText
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const existingLabels = new Set(
                          baseChoices.map((c) =>
                            typeof c === 'string'
                              ? c
                              : ((c as { label?: string })?.label ?? ''),
                          ),
                        );
                        const merged = [...baseChoices];

                        for (const v of cellValues) {
                          if (!existingLabels.has(v)) {
                            existingLabels.add(v);
                            merged.push(v);
                          }
                        }

                        return merged;
                      })()
                    : baseChoices;

                  const needsUpdate =
                    node.attrs.dataType !== dataType ||
                    node.attrs.title !== title ||
                    JSON.stringify(node.attrs.choices || []) !==
                      JSON.stringify(choices);

                  if (needsUpdate) {
                    cellsToUpdate.push({
                      pos,
                      attrs: {
                        ...node.attrs,
                        dataType,
                        choices,
                        title,
                      },
                    });
                  }
                } else if (sectionType === 'MULTI_QUESTION') {
                  // MULTI_QUESTION: match cell to question by data-id or row position
                  const questions = config.questions || [];
                  let matchingQuestion: any = null;

                  if (dataId) {
                    matchingQuestion = questions.find(
                      (q: any) => q.id === dataId,
                    );
                  } else if (tableNode) {
                    try {
                      const map = TableMap.get(tableNode);
                      const cellPosInTable = pos - (tablePos + 1);
                      let rowIndex = -1;

                      try {
                        const r = map.findCell(cellPosInTable + 1);

                        rowIndex = r.top;
                      } catch {
                        for (let i = 0; i < map.map.length; i++) {
                          const cellStart = map.map[i];

                          if (cellPosInTable >= cellStart) {
                            rowIndex = Math.floor(i / map.width);
                          }
                        }
                      }

                      if (rowIndex >= 0 && rowIndex % 2 === 1) {
                        const questionIndex = Math.floor(rowIndex / 2);

                        if (
                          questionIndex >= 0 &&
                          questionIndex < questions.length
                        ) {
                          matchingQuestion = questions[questionIndex];
                        }
                      }
                    } catch {
                      return;
                    }
                  }

                  if (
                    matchingQuestion &&
                    matchingQuestion.dataType &&
                    matchingQuestion.question
                  ) {
                    const needsUpdate =
                      node.attrs.dataType !== matchingQuestion.dataType ||
                      node.attrs.title !== matchingQuestion.question ||
                      JSON.stringify(node.attrs.choices || []) !==
                        JSON.stringify(matchingQuestion.choices || []);

                    if (needsUpdate) {
                      cellsToUpdate.push({
                        pos,
                        attrs: {
                          ...node.attrs,
                          dataType: matchingQuestion.dataType,
                          choices: matchingQuestion.choices || [],
                          title: matchingQuestion.question,
                          nodeAttrs: {
                            ...node.attrs.nodeAttrs,
                            'data-id': matchingQuestion.id,
                          },
                        },
                      });
                    }
                  }
                }
              }
            });

            // If no updates needed, return null
            if (cellsToUpdate.length === 0) {
              return null;
            }

            // Limit updates per transaction to prevent overwhelming the editor
            const MAX_UPDATES = 50;
            const updatesToApply = cellsToUpdate.slice(0, MAX_UPDATES);

            // Apply updates
            const tr = newState.tr;

            for (const update of updatesToApply) {
              tr.setNodeMarkup(update.pos, undefined, update.attrs);
            }

            tr.setMeta('syncMultiQuestionTableCells', true);

            // Update the last update time
            lastUpdateTime = Date.now();

            return tr;
          },
        });
      })(),
    ];
  },
});
